import { Helmet } from 'react-helmet-async';

const defaultMeta = {
    title: 'Aitrixy - Search Visibility & AI Discovery Agency',
    description: 'Rank on Google. Dominate Maps. Show up in AI. Strategic SEO, GMB optimization, and AI search visibility for businesses that want to lead.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&auto=format&fit=crop&q=80',
    url: 'https://aitrixy.com',
};

export const SEO = ({ 
    title, 
    description, 
    image, 
    url,
    type = 'website',
    schema
}) => {
    const metaTitle = title ? `${title} | Aitrixy` : defaultMeta.title;
    const metaDescription = description || defaultMeta.description;
    const metaImage = image || defaultMeta.image;
    const metaUrl = url || defaultMeta.url;

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{metaTitle}</title>
            <meta name="title" content={metaTitle} />
            <meta name="description" content={metaDescription} />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={metaUrl} />
            <meta property="og:title" content={metaTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:image" content={metaImage} />
            
            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={metaUrl} />
            <meta property="twitter:title" content={metaTitle} />
            <meta property="twitter:description" content={metaDescription} />
            <meta property="twitter:image" content={metaImage} />
            
            {/* Canonical URL */}
            <link rel="canonical" href={metaUrl} />
            
            {/* Additional Schema.org structured data */}
            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            )}
        </Helmet>
    );
};

// Pre-defined SEO configs for pages
export const pagesSEO = {
    home: {
        title: null, // Uses default
        description: 'Rank on Google. Dominate Maps. Show up in AI. Strategic SEO, GMB optimization, and AI search visibility for businesses that want to lead.',
    },
    about: {
        title: 'About Us',
        description: 'Learn about Aitrixy - the next-generation search visibility agency helping businesses dominate Google, Maps, and AI search platforms.',
    },
    services: {
        title: 'Our Services',
        description: 'Complete search domination services: GMB Optimization, GEO SEO, AI Search Optimization, High-Intent SEO, and Programmatic SEO.',
    },
    'case-studies': {
        title: 'Case Studies',
        description: 'Real results from real businesses. See how Aitrixy has helped 300+ businesses achieve measurable growth in search visibility.',
    },
    blog: {
        title: 'Blog',
        description: 'Expert insights on SEO, local search, AI optimization, and digital growth strategies from the Aitrixy team.',
    },
    contact: {
        title: 'Contact Us',
        description: 'Get in touch with Aitrixy for a free strategy consultation. Let us help you dominate search.',
    },
    'gmb-optimization-services': {
        title: 'GMB Optimization Services',
        description: 'Dominate Google Maps with our comprehensive GMB optimization services. Rank #1 in local searches.',
    },
    'geo-seo-services': {
        title: 'GEO SEO Services',
        description: 'Location-based SEO to rank for city + service keywords. Capture high-intent local traffic.',
    },
    'ai-search-optimization': {
        title: 'AI Search Optimization',
        description: 'Get featured in ChatGPT, Google SGE, Perplexity. Future-proof your search visibility.',
    },
    'seo-services': {
        title: 'High-Intent SEO Services',
        description: 'Revenue-driven SEO targeting commercial keywords that convert. Grow your business with qualified traffic.',
    },
    'programmatic-seo': {
        title: 'Programmatic SEO',
        description: 'Scale to 1000+ optimized pages automatically. Capture massive long-tail traffic efficiently.',
    },
    'healthcare-seo': {
        title: 'Healthcare SEO',
        description: 'Help patients find your hospital or clinic online. Specialized SEO for healthcare providers.',
    },
    'clinic-seo': {
        title: 'Clinic SEO',
        description: 'Fill your appointment book with qualified patients through targeted local SEO strategies.',
    },
    'travel-seo': {
        title: 'Travel SEO',
        description: 'Rank for destination and package keywords. Compete with OTAs and capture travel bookings.',
    },
    'visa-agency-seo': {
        title: 'Visa Agency SEO',
        description: 'Capture visa seekers searching online. Build trust and dominate visa-related searches.',
    },
    'local-business-seo': {
        title: 'Local Business SEO',
        description: 'Be the #1 choice in your neighborhood. Dominate local search and Google Maps.',
    },
};
